import { SquarePen, GitCompare, ListPlus } from 'lucide-react';
import type { ReactElement } from 'react';
import type { Inventory, UnitCode } from '~/graphql/graphql';
import { formatQuantityCell } from '~/utils';
import { ActionRow } from './ActionRow';

export type TFn = (key: string, options?: Record<string, unknown>) => string;

export type CompareButtonOptions = {
  label?: string;
  variant?: 'filled' | 'outlined';
};

export type ActionButtonsOptions = {
  testIdSuffix: string | number;
  navigate: (path: string) => void;
  onOpenEdit: (inventory: Inventory) => void;
  compareOptions?: CompareButtonOptions;
  onAddToBuyList?: (inventory: Inventory) => void;
};

/** The order-builder Compare deep link — single source for its URL shape. */
export function buildCompareUrl(args: {
  inventoryId: string;
  platformProductLengthId: string;
  locationId: string;
  platformProductId: string;
}): string {
  return `/order-builder/view?inventoryId=${args.inventoryId}&platformProductLengthId=${args.platformProductLengthId}&locationId=${args.locationId}&platformProductId=${args.platformProductId}&returnPath=${window.location.pathname}`;
}

export function getCompareButton(
  testId: string,
  compareUrl: string,
  t: TFn,
  navigate: (path: string) => void,
  options?: CompareButtonOptions
): ReactElement {
  const variantClass =
    options?.variant === 'filled'
      ? 'bg-prim text-neut-0 hover:bg-prim-hover'
      : 'border border-prim text-prim hover:bg-emerald-50 active:bg-emerald-100';
  return (
    <button
      type="button"
      data-testid={testId}
      className={`flex items-center gap-2 px-3 py-1.5 ${variantClass} text-xs font-medium rounded-lg transition-colors whitespace-nowrap`}
      title={options?.label ?? t('inventory.compare')}
      onClick={() => navigate(compareUrl)}
    >
      <GitCompare size={16} aria-hidden="true" />
      {options?.label ?? t('inventory.compare')}
    </button>
  );
}

export function getAddToBuyListButton(
  testId: string,
  onAddToBuyList: () => void,
  t: TFn
): ReactElement {
  const label = t('inventory.buy_list.add_action');
  return (
    <button
      type="button"
      data-testid={testId}
      className="flex items-center gap-1.5 px-3 py-1.5 border border-prim text-prim hover:bg-emerald-50 active:bg-emerald-100 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
      onClick={onAddToBuyList}
      title={label}
      aria-label={label}
    >
      <ListPlus size={16} aria-hidden="true" />
      {label}
    </button>
  );
}

export function getActionButtons(
  inv: Inventory,
  t: TFn,
  {
    testIdSuffix,
    navigate,
    onOpenEdit,
    compareOptions,
    onAddToBuyList,
  }: ActionButtonsOptions
): ReactElement {
  return (
    <ActionRow>
      <button
        type="button"
        data-testid={`edit-inv-${testIdSuffix}`}
        className="p-1.5 text-neut-600 hover:text-prim-500 hover:bg-neut-100 rounded transition-colors"
        onClick={() => onOpenEdit(inv)}
        title={t('inventory.edit')}
      >
        <SquarePen size={16} aria-hidden="true" />
      </button>
      {onAddToBuyList &&
        getAddToBuyListButton(
          `add-to-buy-list-${testIdSuffix}`,
          () => onAddToBuyList(inv),
          t
        )}
      {getCompareButton(
        `view-inv-${testIdSuffix}`,
        buildCompareUrl({
          inventoryId: inv.id,
          platformProductLengthId: inv.platformProductsLength.id,
          locationId: inv.location.id,
          platformProductId: inv.platformProductsLength.platformProduct.id,
        }),
        t,
        navigate,
        compareOptions
      )}
    </ActionRow>
  );
}

export function getWeeksCovered(
  weeksCovered: number,
  weeksCoveredBeforeUrgent: number,
  weeksCoveredBeforeMonitor: number
): ReactElement {
  if (!Number.isFinite(weeksCovered)) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
        Infinity
      </span>
    );
  }

  const label = `${weeksCovered.toFixed(2)} weeks`;
  if (weeksCovered <= weeksCoveredBeforeUrgent) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium text-error-700 bg-error-100">
        {label}
      </span>
    );
  }

  if (weeksCovered <= weeksCoveredBeforeMonitor) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium text-warning-700 bg-warning-100">
        {label}
      </span>
    );
  }

  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
      {label}
    </span>
  );
}

export function getStockCell(
  availableStock: number,
  unit: UnitCode
): ReactElement {
  const isOversold = availableStock < 0;
  return (
    <span
      data-testid="available-stock"
      className={isOversold ? 'text-error-700 font-medium' : undefined}
    >
      {formatQuantityCell(availableStock, unit)}
    </span>
  );
}
